import React, { useState, useEffect } from 'react';
import { 
  Drawer, 
  Button, 
  Space, 
  Card, 
  Switch, 
  Select, 
  Divider, 
  Typography, 
  message,
  Popconfirm,
  Input,
  Row,
  Col,
  Tag
} from 'antd';
import { 
  SettingOutlined, 
  EyeOutlined, 
  EyeInvisibleOutlined,
  MinusOutlined,
  ExpandOutlined,
  DeleteOutlined,
  SaveOutlined,
  DownloadOutlined,
  UploadOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { layoutPreferencesService, UserPreferences, DashboardLayout, WidgetConfig } from '../services/layoutPreferences';

const { Title, Text } = Typography;
const { Option } = Select;

interface LayoutCustomizerProps {
  visible: boolean;
  onClose: () => void;
  onLayoutChange: (layoutId: string) => void;
  currentLayout: DashboardLayout | null;
}

export const LayoutCustomizer: React.FC<LayoutCustomizerProps> = ({
  visible,
  onClose,
  onLayoutChange,
  currentLayout
}) => {
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [newLayoutName, setNewLayoutName] = useState('');
  const [saveMode, setSaveMode] = useState(false);

  useEffect(() => {
    if (visible) {
      loadPreferences();
    }
  }, [visible]);

  const loadPreferences = () => {
    const prefs = layoutPreferencesService.getPreferences();
    setPreferences(prefs);
  };

  const handleLayoutSwitch = (layoutId: string) => {
    layoutPreferencesService.switchLayout(layoutId);
    onLayoutChange(layoutId);
    loadPreferences();
    message.success('Layout değiştirildi');
  };

  const handleWidgetVisibilityToggle = (widgetId: string) => {
    layoutPreferencesService.toggleWidgetVisibility(widgetId);
    onLayoutChange(preferences?.currentLayout || 'ai-focused');
    loadPreferences();
  };

  const handleWidgetMinimizeToggle = (widgetId: string) => {
    layoutPreferencesService.toggleWidgetMinimized(widgetId);
    onLayoutChange(preferences?.currentLayout || 'ai-focused');
    loadPreferences();
  };

  const handleSaveAsNew = () => {
    if (!newLayoutName.trim()) {
      message.error('Layout adı gerekli');
      return;
    }

    if (!currentLayout) {
      message.error('Aktif layout bulunamadı');
      return;
    }

    const newLayout: DashboardLayout = {
      ...currentLayout,
      id: `custom-${Date.now()}`,
      name: newLayoutName.trim(),
      isDefault: false
    };

    layoutPreferencesService.saveCustomLayout(newLayout);
    layoutPreferencesService.switchLayout(newLayout.id);
    onLayoutChange(newLayout.id);
    loadPreferences();
    
    setNewLayoutName('');
    setSaveMode(false);
    message.success('Yeni layout kaydedildi');
  };

  const handleDeleteLayout = (layoutId: string) => {
    layoutPreferencesService.deleteLayout(layoutId);
    loadPreferences();
    message.success('Layout silindi');
  };

  const handleResetToDefault = () => {
    layoutPreferencesService.resetToDefault();
    onLayoutChange('ai-focused');
    loadPreferences();
    message.success('Varsayılan layout\'a sıfırlandı');
  };

  const handleExportLayout = () => {
    if (!currentLayout) return;
    
    try {
      const exportData = layoutPreferencesService.exportLayout(currentLayout.id);
      const blob = new Blob([exportData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `layout-${currentLayout.name}.json`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('Layout dışa aktarıldı');
    } catch (error) {
      message.error('Dışa aktarma hatası');
    }
  };

  const handleImportLayout = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        layoutPreferencesService.importLayout(content);
        loadPreferences();
        message.success('Layout içe aktarıldı');
      } catch (error) {
        message.error('Geçersiz layout dosyası');
      }
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset input
  };

  const getWidgetDisplayName = (type: WidgetConfig['type']): string => {
    const names = {
      'ai-realtime': '🤖 Gerçek Zamanlı AI',
      'ai-learning': '🧠 AI Öğrenme',
      'ai-stats': '📊 AI İstatistikleri',
      'system-stats': '💻 Sistem İstatistikleri',
      'verification-stats': '🔍 Doğrulama İstatistikleri',
      'user-activity': '👥 Kullanıcı Aktivitesi',
      'recent-users': '🆕 Son Kullanıcılar',
      'system-status': '⚡ Sistem Durumu',
      'performance': '📈 Performans'
    };
    return names[type] || type;
  };

  if (!preferences || !currentLayout) {
    return null;
  }

  return (
    <Drawer
      title={
        <Space>
          <SettingOutlined />
          <span>Dashboard Özelleştirme</span>
        </Space>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      width={400}
    >
      {/* Layout Selection */}
      <div style={{ marginBottom: 24 }}>
        <Title level={4}>📐 Layout Seçimi</Title>
        <Select
          style={{ width: '100%' }}
          value={preferences.currentLayout}
          onChange={handleLayoutSwitch}
        >
          {preferences.layouts.map(layout => (
            <Option key={layout.id} value={layout.id}>
              <Space>
                <span>{layout.name}</span>
                {layout.isDefault && <Tag color="blue" size="small">Varsayılan</Tag>}
              </Space>
            </Option>
          ))}
        </Select>
      </div>

      <Divider />

      {/* Widget Configuration */}
      <div style={{ marginBottom: 24 }}>
        <Title level={4}>🧩 Widget Ayarları</Title>
        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {currentLayout.widgets.map(widget => (
            <Card 
              key={widget.id} 
              size="small" 
              style={{ marginBottom: 8 }}
              title={
                <Space>
                  <span style={{ fontSize: '12px' }}>
                    {getWidgetDisplayName(widget.type)}
                  </span>
                  {widget.minimized && <Tag color="orange" size="small">Küçültülmüş</Tag>}
                </Space>
              }
              extra={
                <Space>
                  <Button
                    type="text"
                    size="small"
                    icon={widget.visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                    onClick={() => handleWidgetVisibilityToggle(widget.id)}
                    title={widget.visible ? 'Gizle' : 'Göster'}
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={widget.minimized ? <ExpandOutlined /> : <MinusOutlined />}
                    onClick={() => handleWidgetMinimizeToggle(widget.id)}
                    title={widget.minimized ? 'Genişlet' : 'Küçült'}
                  />
                </Space>
              }
            >
              <Row gutter={8}>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    Konum: {widget.position.x},{widget.position.y}
                  </Text>
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    Boyut: {widget.position.w}×{widget.position.h}
                  </Text>
                </Col>
              </Row>
            </Card>
          ))}
        </div>
      </div>

      <Divider />

      {/* Save Options */}
      <div style={{ marginBottom: 24 }}>
        <Title level={4}>💾 Layout İşlemleri</Title>
        
        {!saveMode ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Button 
              type="primary" 
              icon={<SaveOutlined />}
              block
              onClick={() => setSaveMode(true)}
            >
              Yeni Layout Olarak Kaydet
            </Button>
            
            <Row gutter={8}>
              <Col span={12}>
                <Button 
                  icon={<DownloadOutlined />}
                  block
                  onClick={handleExportLayout}
                >
                  Dışa Aktar
                </Button>
              </Col>
              <Col span={12}>
                <Button 
                  icon={<UploadOutlined />}
                  block
                  onClick={() => document.getElementById('layout-import')?.click()}
                >
                  İçe Aktar
                </Button>
                <input
                  id="layout-import"
                  type="file"
                  accept=".json"
                  style={{ display: 'none' }}
                  onChange={handleImportLayout}
                />
              </Col>
            </Row>
          </Space>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Input
              placeholder="Layout adını girin..."
              value={newLayoutName}
              onChange={(e) => setNewLayoutName(e.target.value)}
              onPressEnter={handleSaveAsNew}
            />
            <Row gutter={8}>
              <Col span={12}>
                <Button 
                  type="primary" 
                  block
                  onClick={handleSaveAsNew}
                >
                  Kaydet
                </Button>
              </Col>
              <Col span={12}>
                <Button 
                  block
                  onClick={() => {
                    setSaveMode(false);
                    setNewLayoutName('');
                  }}
                >
                  İptal
                </Button>
              </Col>
            </Row>
          </Space>
        )}
      </div>

      <Divider />

      {/* Custom Layout Management */}
      <div style={{ marginBottom: 24 }}>
        <Title level={4}>🗂️ Özel Layout'lar</Title>
        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {preferences.layouts
            .filter(layout => !layout.isDefault)
            .map(layout => (
              <Card 
                key={layout.id}
                size="small"
                style={{ marginBottom: 8 }}
                title={<span style={{ fontSize: '12px' }}>{layout.name}</span>}
                extra={
                  <Popconfirm
                    title="Bu layout'u silmek istediğinizden emin misiniz?"
                    onConfirm={() => handleDeleteLayout(layout.id)}
                    okText="Evet"
                    cancelText="Hayır"
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<DeleteOutlined />}
                      danger
                    />
                  </Popconfirm>
                }
              >
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {layout.widgets.length} widget • {new Date(layout.createdAt).toLocaleDateString('tr-TR')}
                </Text>
              </Card>
            ))}
          
          {preferences.layouts.filter(layout => !layout.isDefault).length === 0 && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Henüz özel layout oluşturulmamış
            </Text>
          )}
        </div>
      </div>

      <Divider />

      {/* Reset Option */}
      <Popconfirm
        title="Varsayılan layout'a sıfırlamak istediğinizden emin misiniz?"
        onConfirm={handleResetToDefault}
        okText="Evet"
        cancelText="Hayır"
      >
        <Button 
          danger 
          icon={<ReloadOutlined />}
          block
        >
          Varsayılan Layout'a Sıfırla
        </Button>
      </Popconfirm>
    </Drawer>
  );
};
